/**
 * Shared locale-prefix grammar for blog URLs (client-side ESM mirror).
 *
 * Byte-equivalent ESM mirror of
 * `@puffin-core/marketing-cms/lib/utils/blogLocalePath.js` (CJS, server-side).
 * Both copies define exactly the same `LOCALE_PREFIX_PATTERN` regex +
 * helpers so the consumer serve layer (CJS) and the `<LocaleSwitcher>`
 * blog-aware route rewriter (ESM React) agree on what counts as a blog
 * locale prefix. The cross-app contract
 * `tests/cross-app/contracts/blogLocalePathParity.contract.test.js` locks
 * the two copies' regexes + helper shapes at byte level.
 *
 * Duplication rationale: marketing-cms is server-only CJS (imports knex
 * etc.); marketing-shell is client React ESM. A direct cross-package
 * import would pull the CJS interop boundary into every React-app
 * bundle. The util is small (regex + helpers, no deps) so the
 * duplication-with-cross-app-lock is the simplest factory-first move.
 *
 * @module @puffin-core/marketing-shell/blog/blogLocalePath
 */

/**
 * See `@puffin-core/marketing-cms/lib/utils/blogLocalePath.js` for the
 * canonical grammar documentation. Both copies MUST keep this regex byte-
 * identical (locked by `blogLocalePathParity.contract.test.js`).
 */
export const LOCALE_PREFIX_PATTERN = /^\/([a-z]{2}(?:-[a-z]{2})?)(\/|$)/;

/** Bare-token grammar (no leading slash) — client-side route rewriters. */
export const LOCALE_TOKEN_PATTERN = /^[a-z]{2}(-[a-z]{2})?$/;

/**
 * Strip a `/<locale>` prefix from a path, returning `{ locale, path }`.
 * When the path carries no recognized locale prefix, returns
 * `{ locale: defaultLocale, path }` unchanged.
 */
export function stripLocalePrefix(rawPath, defaultLocale = 'en') {
  if (typeof rawPath !== 'string' || !rawPath) {
    return { locale: defaultLocale, path: '/' };
  }
  const match = rawPath.match(LOCALE_PREFIX_PATTERN);
  if (!match) {
    return { locale: defaultLocale, path: rawPath };
  }
  const locale = match[1];
  const stripped = rawPath.slice(locale.length + 1) || '/';
  return { locale, path: stripped };
}

/**
 * Rewrite a (canonical, locale-stripped) path for a target locale.
 *
 *   - target === default → return canonical path as-is
 *   - otherwise → prefix with `/<targetLocale>`
 */
export function withLocalePrefix(canonicalPath, targetLocale, defaultLocale = 'en') {
  if (typeof canonicalPath !== 'string' || !canonicalPath) return '/';
  if (
    typeof targetLocale !== 'string' ||
    !LOCALE_TOKEN_PATTERN.test(targetLocale) ||
    targetLocale === defaultLocale
  ) {
    return canonicalPath;
  }
  const normalized = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return `/${targetLocale}${normalized}`;
}

/**
 * Predicate — true when `path` is a blog route (with or without a
 * locale prefix). Used by the LocaleSwitcher to gate the URL rewrite:
 * picking a locale on a non-blog page changes language only; picking
 * one on a blog page also navigates to the corresponding native URL.
 */
export function isBlogPath(rawPath) {
  if (typeof rawPath !== 'string' || !rawPath) return false;
  const { path } = stripLocalePrefix(rawPath);
  return path === '/blog' || path.startsWith('/blog/');
}
